/**
 * Settings Page - Modern, Clean Design
 * 
 * Features:
 * - Clean sidebar navigation
 * - Rich content sections
 * - Proper light/dark mode support
 * - Professional, minimalist aesthetic
 */

import React, { useState, type ReactNode, useMemo } from "react"
import {
    BiMoney,
    BiTask,
    BsGear,
    BsKey,
    BsPerson,
    BsTools,
    Button,
    ConditionalRenderWithElse,
    FaSignOutAlt,
    BiChevronRight,
} from "@acme/ui";
import { useRouter } from "next/router";
import { Account } from "./SettingsSection/Account";
import { Preference } from "./SettingsSection/Preference";
import { Plans } from "./SettingsSection/Plans";
import { ToolsSettings } from "./SettingsSection/ToolsSettings";
import { api } from "@acme/client";
import { signIn, signOut } from "next-auth/react";
import { EXTENSION_ID } from "@acme/util";
import { useSetAtom } from "jotai";
import { deleteNonSyncAtom } from "../store/setting";
import { RoadmapSection } from "./SettingsSection/Roadmap";
import { Profile } from "./SettingsSection/Profile";

// =============================================================================
// MENU CONFIGURATION
// =============================================================================

const menus = [
    {
        title: 'API Keys',
        description: 'Manage your AI provider keys',
        path: 'api',
        icon: <BsKey className="text-lg" />,
    },
    {
        title: 'Tools & MCP',
        description: 'Configure tools and servers',
        path: 'tools',
        icon: <BsTools className="text-lg" />,
    },
    {
        title: 'Preferences',
        description: 'Customize your experience',
        path: 'preference',
        icon: <BsGear className="text-lg" />,
    },
    {
        title: 'Account',
        description: 'Profile and authentication',
        path: 'user',
        icon: <BsPerson className="text-lg" />,
    },
    {
        title: 'Subscription',
        description: 'Plans and billing',
        path: 'plan',
        icon: <BiMoney className="text-lg" />,
    },
    {
        title: 'Support',
        description: 'Roadmap and help',
        path: 'roadmap',
        icon: <BiTask className="text-lg" />,
    },
]

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const SettingsPage = () => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleteConfirm, setIsDeleteConfirm] = useState(false);
    const { data: session } = api.auth.getSession.useQuery();
    const onDeleteData = useSetAtom(deleteNonSyncAtom)

    const router = useRouter();

    const activeMenu = useMemo(() => {
        const { menu } = router.query;
        return menu || 'api';
    }, [router.query]);

    const activeMenuConfig = useMemo(() => {
        return menus.find(m => m.path === activeMenu) || menus[0];
    }, [activeMenu]);

    const onSignOut = () => {
        try {
            if (chrome.runtime && chrome?.runtime?.sendMessage) {
                const extensionId = EXTENSION_ID
                chrome.runtime.sendMessage(
                    extensionId,
                    { type: 'set-signout' },
                    () => { /* callback */ }
                );
            }
            setShowDeleteModal(false)
            setIsDeleteConfirm(true)
        } catch (error) {
            setIsLoading(false)
        }
    }

    const onDeleteDataSync = async (removeNonSync?: boolean) => {
        setIsLoading(true)
        onDeleteData({ removeNonSync })
        await signOut()
        setIsLoading(false)
        setIsDeleteConfirm(false)
        router.reload()
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-neutral-800">
            {/* Header */}
            <div className="border-b border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-800">
                <div className="max-w-6xl mx-auto px-6 py-6">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Settings
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Manage your account and preferences
                    </p>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-6xl mx-auto px-6 py-8">
                <div className="grid grid-cols-12 gap-8">
                    {/* Sidebar */}
                    <div className="col-span-12 lg:col-span-3">
                        <div className="lg:sticky lg:top-6">
                            <nav className="space-y-1">
                                {menus.map(menu => (
                                    <MenuItem
                                        key={menu.path}
                                        title={menu.title}
                                        description={menu.description}
                                        icon={menu.icon}
                                        path={menu.path}
                                        active={activeMenu === menu.path}
                                        onClick={() => void router.push(`/settings?menu=${menu.path}`)}
                                    />
                                ))}
                            </nav>

                            {/* User Info */}
                            {session?.user && (
                                <div className="mt-6 p-4 rounded-xl bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium">
                                            {session.user.name?.charAt(0).toUpperCase() || 'U'}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                {session.user.name}
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                {session.user.email}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="col-span-12 lg:col-span-9">
                        <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 overflow-hidden">
                            {/* Section Header */}
                            <div className="px-6 py-5 border-b border-gray-200 dark:border-neutral-800">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-neutral-800 flex items-center justify-center text-gray-600 dark:text-gray-200">
                                        {activeMenuConfig?.icon}
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                            {activeMenuConfig?.title}
                                        </h2>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            {activeMenuConfig?.description}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Section Content */}
                            <div className="p-6">
                                <ConditionalRenderWithElse
                                    show={(activeMenu === 'user' || activeMenu === 'plan') && !session}
                                    elseChildren={
                                        <>
                                            {activeMenu === 'user' && (
                                                <div className="space-y-6">
                                                    <Profile session={session} />
                                                    <div className="pt-6 border-t border-gray-200 dark:border-neutral-800">
                                                        <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
                                                            Session
                                                        </h3>
                                                        <Button
                                                            loading={isLoading}
                                                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                                                            onClick={() => setShowDeleteModal(true)}
                                                        >
                                                            <FaSignOutAlt className="text-sm" />
                                                            <span>Sign Out</span>
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                            {activeMenu === 'plan' && <Plans session={session} />}
                                        </>
                                    }
                                >
                                    <SignInPrompt onSignIn={() => void signIn()} />
                                </ConditionalRenderWithElse>

                                {activeMenu === 'preference' && <Preference session={session} />}
                                {activeMenu === 'api' && <Account />}
                                {activeMenu === 'tools' && <ToolsSettings />}
                                {activeMenu === 'roadmap' && <RoadmapSection />}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sign Out Modal */}
            {showDeleteModal && (
                <Modal
                    title="Sign Out"
                    description="Are you sure you want to sign out of your account?"
                    onClose={() => setShowDeleteModal(false)}
                >
                    <div className="flex justify-end gap-3 mt-6">
                        <button
                            className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                            onClick={() => setShowDeleteModal(false)}
                        >
                            Cancel
                        </button>
                        <Button
                            loading={isLoading}
                            className="px-4 py-2 text-sm font-medium !text-white !bg-red-500 hover:!bg-red-600 rounded-lg transition-colors"
                            onClick={() => void onSignOut()}
                        >
                            Sign Out
                        </Button>
                    </div>
                </Modal>
            )}

            {/* Delete Data Confirmation Modal */}
            {isDeleteConfirm && (
                <Modal
                    title="Delete Local Data?"
                    description="You have non-synced data (chat sessions, notes, etc.). Do you want to delete this local data as well?"
                    onClose={() => void onDeleteDataSync(false)}
                >
                    <div className="flex justify-end gap-3 mt-6">
                        <Button
                            className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-neutral-700 rounded-lg hover:bg-gray-200 dark:hover:bg-neutral-600 transition-colors"
                            onClick={() => void onDeleteDataSync(false)}
                        >
                            Keep Data
                        </Button>
                        <Button
                            loading={isLoading}
                            className="px-4 py-2 text-sm font-medium !text-white !bg-red-500 hover:!bg-red-600 rounded-lg transition-colors"
                            onClick={() => void onDeleteDataSync(true)}
                        >
                            Delete All
                        </Button>
                    </div>
                </Modal>
            )}
        </div>
    );
};

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

interface MenuItemProps {
    title: string;
    description: string;
    icon: ReactNode;
    path: string;
    active: boolean;
    onClick: () => void;
}

const MenuItem = ({ title, description, icon, active, onClick }: MenuItemProps) => (
    <button
        onClick={onClick}
        className={`
            w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all
            ${active
                ? 'bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 shadow-sm'
                : 'hover:bg-white dark:hover:bg-neutral-800 border border-transparent'
            }
        `}
    >
        <div className={`
            w-9 h-9 rounded-lg flex items-center justify-center
            ${active
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 dark:bg-neutral-700 text-gray-500 dark:text-gray-400'
            }
        `}>
            {icon}
        </div>
        <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium ${active ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                {title}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {description}
            </p>
        </div>
        <BiChevronRight className={`text-lg ${active ? 'text-blue-500' : 'text-gray-400'}`} />
    </button>
);

interface ModalProps {
    title: string;
    description: string;
    children: ReactNode;
    onClose: () => void;
}

const Modal = ({ title, description, children, onClose }: ModalProps) => (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center">
        <div
            className="absolute inset-0 bg-black/50"
            onClick={onClose}
        />
        <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 border border-gray-200 dark:border-neutral-800">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {title}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                {description}
            </p>
            {children}
        </div>
    </div>
);

const SignInPrompt = ({ onSignIn }: { onSignIn: () => void }) => (
    <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-neutral-700 flex items-center justify-center">
            <BsPerson className="text-2xl text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Sign in required
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">
            Sign in to access your account settings, subscription, and sync your data across devices.
        </p>
        <Button
            className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium !text-white !bg-blue-500 hover:!bg-blue-600 rounded-xl transition-colors"
            onClick={onSignIn}
        >
            <FaSignOutAlt className="text-sm" />
            Sign In
        </Button>
    </div>
);

export default SettingsPage;
